import Role from "../models/role.model";
import Permission from "../../../Modules/Permission/models/Permission.model";
import { Service } from "typedi";
import { HttpException } from "../../../utils/exceptions/httpException";
import { TFunction } from "i18next";
import { status } from "../../../utils/helpers/api.responses";
import { Types } from "mongoose";

@Service()
export class RoleService {
    public async createRole(name: string, description: string | undefined, permissions: string[], t: TFunction) {
        try {
            if (!permissions || permissions.length === 0) {
                throw new HttpException(status.BadRequest, t('General.empty', { field: t('Role.permissions') }));
            }

            const slug = name.trim().toLowerCase().replace(/\s+/g, '-');

            const existing = await Role.findOne({ slug });

            if (existing) {
                if (!existing.isDeleted) {
                    throw new HttpException(status.ResourceExist, t('General.already_exist', { field: t('Role.role') }));
                }

                const validPermissions = await Permission.find({ _id: { $in: permissions }, isActive: true });
                if (validPermissions.length !== permissions.length) {
                    throw new HttpException(status.BadRequest, t('Role.invalid_permissions'));
                }

                existing.name = name;
                existing.description = description;
                existing.slug = slug;
                existing.permissions = permissions.map(id => new Types.ObjectId(id));
                existing.isDeleted = false;
                existing.deletedAt = null;

                await existing.save();
                return existing;
            }

            const validPermissions = await Permission.find({ _id: { $in: permissions }, isActive: true });
            if (validPermissions.length !== permissions.length) {
                throw new HttpException(status.BadRequest, t('Role.invalid_permissions'));
            }

            const newRole = new Role({ name, description, permissions, slug });
            await newRole.save();
            return newRole;

        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorCreating', { field: t('Role.role') }));
        }
    }

    public async getRoleById(id: string, t: TFunction) {
        try {
            const role = await Role.findOne({ _id: id, isDeleted: false }).populate({
                path: 'permissions',
                match: { isActive: true },
            });

            if (!role) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Role.role') }));
            }

            return role;
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Role.role') }));
        }
    }

    public async updateRole(id: string, updates: any, t: TFunction) {
        try {
            const existingRole = await Role.findOne({ _id: id, isDeleted: false });
            if (!existingRole) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Role.role') }));
            }

            if (updates.permissions) {
                const validPermissions = await Permission.find({ _id: { $in: updates.permissions }, isActive: true });
                if (validPermissions.length !== updates.permissions.length) {
                    throw new HttpException(status.BadRequest, t('Role.invalid_permissions'));
                }
            }

            if (updates.name) {
                const newSlug = updates.name.trim().toLowerCase().replace(/\s+/g, '-');
                if (newSlug !== existingRole.slug) {
                    const slugExists = await Role.findOne({ slug: newSlug, isDeleted: false });
                    if (slugExists) {
                        throw new HttpException(status.ResourceExist, t('General.already_exist', { field: t('Role.role') }));
                    }
                    updates.slug = newSlug;
                }
            }

            const updatedRole = await Role.findByIdAndUpdate(id, updates, { new: true });
            return updatedRole;

        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('Role.role') }));
        }
    }

    public async deleteRole(id: string, t: TFunction) {
        try {
            const role = await Role.findOne({ _id: id, isDeleted: false });
            if (!role) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Role.role') }));
            }

            role.isDeleted = true;
            role.deletedAt = new Date();
            await role.save();

            return role;
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorDeleting', { field: t('Role.role') }));
        }
    }

    public async getAllRoles(filters: any, page: number, limit: number, sort: any, t: TFunction) {
        try {
            const skip = (page - 1) * limit;

            const query = { ...filters, isDeleted: false };

            const [result, totalCount] = await Promise.all([
                Role.find(query).sort(sort).skip(skip).limit(limit).select('name _id'),
                Role.countDocuments(query),
            ]);

            return {
                data: result,
                total: totalCount,
                page,
                totalPage: Math.ceil(totalCount / limit),
            };
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Role.role') }));
        }
    }
}
